/**
 * useCsvUpload — shared hook for stages that accept CSV file input.
 *
 * Handles file selection, parsing via parseCsv/buildCsvString, and
 * optional row filtering. Returns the raw CSV string for the payload
 * plus parsed columns/rows for preview display.
 */

import { useCallback, useRef, useState } from 'react';
import { parseCsv, buildCsvString } from '../utils';

export interface UseCsvUploadOptions {
  /** Optional filter applied to parsed rows before building the CSV string. */
  filterRows?: (rows: Record<string, string>[]) => Record<string, string>[];
}

export function useCsvUpload(options?: UseCsvUploadOptions) {
  const [columns, setColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [rawCsv, setRawCsv] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseCsv(text);
    const filteredRows = options?.filterRows ? options.filterRows(parsed.rows) : parsed.rows;
    setRawCsv(buildCsvString(parsed.columns, filteredRows));
    setColumns(parsed.columns);
    setRows(filteredRows);
    setFileName(file.name);
  }, [options?.filterRows]);

  const reset = useCallback(() => {
    setRawCsv('');
    setColumns([]);
    setRows([]);
    setFileName(null);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  return {
    columns,
    rows,
    rawCsv,
    setRawCsv,
    fileName,
    fileRef,
    handleFileUpload,
    reset,
  };
}
